
import { useMemo, useRef } from 'react';
import { useTenants } from './queries/useTenants';
import { useUnits, useProperties } from './queries/useProperties';
import { useTickets } from './queries/useMaintenance';
import { useReceivables } from './queries/useAccounting';
import type { Tenant, Unit, Property, MaintenanceTicket, Receivable } from '@repo/shared-types';

interface TenantScopeResult {
  profile: Tenant;
  lease: {
    unit: Unit | undefined;
    property: Property | undefined;
    status: Tenant['status'];
    dates: { start: string; end: string };
  };
  financials: {
    balance: number;
    history: Receivable[];
  };
  maintenance: {
    open: MaintenanceTicket[];
    history: MaintenanceTicket[];
  };
}

export const useTenantScope = (tenantId: string): { scope: TenantScopeResult | null; isLoading: boolean } => {
  const { data: tenants = [], isLoading: tenantsLoading } = useTenants();
  const { data: units = [], isLoading: unitsLoading } = useUnits();
  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  const { data: tickets = [], isLoading: ticketsLoading } = useTickets();
  const { data: receivables = [], isLoading: receivablesLoading } = useReceivables();

  const isLoading =
    tenantsLoading || unitsLoading || propertiesLoading || ticketsLoading || receivablesLoading;

  // Use refs to track previous values and prevent unnecessary recalculations
  const prevTenantIdRef = useRef<string | undefined>(undefined);
  const prevDataHashRef = useRef<string>('');
  const cachedResultRef = useRef<TenantScopeResult | null>(null);

  // Create a stable hash of the data to detect actual changes
  const dataHash = useMemo(() => {
    if (tenantsLoading || unitsLoading || propertiesLoading || ticketsLoading || receivablesLoading) {
      return '';
    }
    return JSON.stringify({
      tenantId,
      tenantsCount: tenants.length,
      unitsCount: units.length,
      propertiesCount: properties.length,
      ticketsCount: tickets.length,
      receivablesCount: receivables.length,
      // Include first few IDs to detect reordering
      tenantIds: tenants.slice(0, 3).map(t => t.id).join(','),
      unitIds: units.slice(0, 3).map(u => u.id).join(','),
    });
  }, [tenantId, tenants, units, properties, tickets, receivables, tenantsLoading, unitsLoading, propertiesLoading, ticketsLoading, receivablesLoading]);

  const scope = useMemo(() => {
    // Early return if still loading or no tenant ID
    if (!tenantId || tenantsLoading || unitsLoading || propertiesLoading || ticketsLoading || receivablesLoading) {
      prevTenantIdRef.current = tenantId;
      prevDataHashRef.current = '';
      cachedResultRef.current = null;
      return null;
    }

    // If tenantId changed, we need to recalculate
    const tenantIdChanged = prevTenantIdRef.current !== tenantId;
    
    // If data hash is the same and tenantId hasn't changed, return cached result
    if (!tenantIdChanged && dataHash === prevDataHashRef.current && cachedResultRef.current) {
      return cachedResultRef.current;
    }

    // Update refs
    prevTenantIdRef.current = tenantId;
    prevDataHashRef.current = dataHash;

    // 1. Get Tenant Profile
    const profile = tenants.find((t) => t.id === tenantId);
    if (!profile) {
      cachedResultRef.current = null;
      return null;
    }

    // 2. Get Unit & Property Context
    const unit = units.find((u) => u.id === profile.unitId);
    const property = properties.find((p) => p.id === profile.propertyId);

    // 3. Filter Financials (Only their payments/charges)
    const paymentHistory = receivables.filter((r) => r.tenantId === tenantId);

    // 4. Filter Maintenance (Only tickets for their unit)
    const myTickets = tickets.filter((t) => t.unitId === profile.unitId);

    const result = {
      profile,
      lease: {
        unit,
        property,
        status: profile.status,
        dates: { start: profile.leaseStart, end: profile.leaseEnd }
      },
      financials: {
        balance: profile.balance,
        history: paymentHistory
      },
      maintenance: {
        open: myTickets.filter((t) => t.status !== 'Completed'),
        history: myTickets
      }
    };

    // Cache the result
    cachedResultRef.current = result;
    return result;
    // Only depend on dataHash and loading states - arrays are accessed but not depended on
    // to prevent infinite loops from React Query reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, dataHash, tenantsLoading, unitsLoading, propertiesLoading, ticketsLoading, receivablesLoading]);

  return { scope, isLoading };
};

